// Interviews API functions
import { config } from '../config/environment';
import { logger } from './logger';

// Prefer environment variable for the Interview Service
// Example: REACT_APP_INTERVIEW_API_BASE=https://<your-interview-service>.azurewebsites.net/api/interview
const INTERVIEW_API_BASE =
  config.interviewApiBase ||
  ''; // Leave empty to force passing apiBase explicitly.

const getAccessToken = async () => {
  // First try to use the global token getter set up by useAuthenticatedApi hook
  if (typeof window !== 'undefined' && window.getAccessToken) {
    try {
      // Determine the correct scope based on environment
      const scope = config.isProduction 
        ? `api://${process.env.REACT_APP_AZURE_AD_CLIENT_ID}/interview-service-prod.access_as_user`
        : `api://${process.env.REACT_APP_AZURE_AD_CLIENT_ID}/interview-service-staging.access_as_user`;
      
      return await window.getAccessToken(scope);
    } catch (error) {
      logger.warn('Global getAccessToken failed, falling back to direct MSAL:', error);
    }
  }
  
  // Fallback to direct MSAL access for non-React contexts
  const { msalInstance } = await import('../config/msalConfig');
  
  // Determine the correct scope based on environment
  const environment = process.env.NODE_ENV || 'development';
  const scope = environment === 'production' 
    ? `api://${process.env.REACT_APP_AZURE_AD_CLIENT_ID}/interview-service-prod.access_as_user`
    : `api://${process.env.REACT_APP_AZURE_AD_CLIENT_ID}/interview-service-staging.access_as_user`;
  
  const accounts = msalInstance.getAllAccounts();
  
  if (accounts.length === 0) {
    throw new Error('No authenticated user found. Please log in first.');
  }
  
  const account = accounts[0];
  
  try {
    // Try to get token silently first with interview-specific scope
    const response = await msalInstance.acquireTokenSilent({
      scopes: [scope],
      account: account,
    });
    return response.accessToken;
  } catch (error) {
    // If silent acquisition fails, try interactive method
    if (error.errorCode === 'consent_required' || 
        error.errorCode === 'interaction_required' || 
        error.errorCode === 'login_required') {
      try {
        const response = await msalInstance.acquireTokenPopup({
          scopes: [scope],
        });
        return response.accessToken;
      } catch (interactiveError) {
        throw new Error(`Failed to acquire access token: ${interactiveError.message}`);
      }
    }
    throw new Error(`Failed to acquire access token: ${error.message}`);
  }
};

/**
 * Fetch interviews by canvassId with pagination.
 * Backend route (Azure Function):
 *   GET {base}/getAllByCanvassId?canvassId=<id>&page=<n>&pageSize=<n>
 * See: Functions_Backend/Interview_Service/src/functions/InterviewChat.ts (get-all-interviews-by-canvassid)
 *
 * @param {Object} options
 * @param {string} options.canvassId - Required canvassId
 * @param {number} [options.page=1]
 * @param {number} [options.pageSize=20]
 * @param {boolean} [options.returnFull=false] - When true, returns full payload with pagination metadata
 * @param {string} [options.apiBase] - Optional override for base URL
 * @returns {Promise<Array|Object>} Array of interviews by default, or full payload when returnFull=true
 */
export const fetchInterviewsByCanvassId = async (options = {}) => {
  const {
    canvassId,
    page = 1,
    pageSize = 20,
    returnFull = false,
    apiBase,
  } = options;

  if (!canvassId) {
    throw new Error('fetchInterviewsByCanvassId: canvassId is required');
  }

  const base = apiBase || INTERVIEW_API_BASE;
  if (!base) {
    throw new Error(
      'Interview API base URL not set. Provide REACT_APP_INTERVIEW_API_BASE or pass options.apiBase.'
    );
  }

  const params = new URLSearchParams();
  params.append('canvassId', String(canvassId));
  params.append('page', String(page));
  params.append('pageSize', String(pageSize));

  const url = `${base}/interview/getAllByCanvassId?${params.toString()}`;

  try {
    // Add authentication headers
    const token = await getAccessToken();
    const headers = { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };

    const res = await fetch(url, {
      method: 'GET',
      headers,
    });
    const result = await res.json();

    if (!res.ok) {
      logger.error('Error fetching interviews by canvassId:', result);
      throw new Error(result?.error || 'Failed to fetch interviews');
    }

    if (!returnFull) {
      // Expecting backend to return { interviews: [{ metadata, messages }], page, pageSize, totalCount, totalPages }
      return result.interviews || [];
    }

    return {
      interviews: result.interviews || [],
      page: result.page ?? page,
      pageSize: result.pageSize ?? pageSize,
      totalCount: result.totalCount ?? null,
      totalPages: result.totalPages ?? null,
      message: result.message || '',
      success: result.success === true,
    };
  } catch (error) {
    logger.error('Exception fetching interviews by canvassId:', error?.message || error);
    throw error;
  }
};

/**
 * Fetch ALL interviews by canvassId (no pagination) for export.
 * Backend route (Azure Function):
 *   GET {base}/interview/export?canvassId=<id>
 *
 * @param {Object} options
 * @param {string} options.canvassId - Required canvassId
 * @param {string} [options.apiBase] - Optional override for base URL
 * @returns {Promise<Array>} All interviews for the canvass
 */
export const fetchAllInterviewsByCanvassId = async (options = {}) => {
  const { canvassId, apiBase } = options;

  if (!canvassId) {
    throw new Error('fetchAllInterviewsByCanvassId: canvassId is required');
  }

  const base = apiBase || INTERVIEW_API_BASE;
  if (!base) {
    throw new Error(
      'Interview API base URL not set. Provide REACT_APP_INTERVIEW_API_BASE or pass options.apiBase.'
    );
  }

  const params = new URLSearchParams();
  params.append('canvassId', String(canvassId));

  const url = `${base}/interview/export?${params.toString()}`;

  try {
    // Add authentication headers
    const token = await getAccessToken();
    const headers = { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };

    const res = await fetch(url, {
      method: 'GET',
      headers,
    });
    const result = await res.json();

    if (!res.ok || result?.success === false) {
      logger.error('Error exporting interviews by canvassId:', result);
      throw new Error(result?.error || 'Failed to export interviews');
    }

    return result.interviews || [];
  } catch (error) {
    logger.error('Exception exporting interviews by canvassId:', error?.message || error);
    throw error;
  }
};

/**
 * Validate an interview for a given sessionId.
 * Backend route (Azure Function):
 *   POST {base}/validate
 * See: Functions_Backend/Interview_Service/src/functions/InterviewChat.ts (user-validate-interview)
 *
 * @param {Object} options
 * @param {string} options.sessionId - Required interview session id
 * @param {boolean} options.isUserValidated - Required flag indicating user validation
 * @param {string} [options.apiBase] - Optional override for base URL
 * @param {string} [options.functionsKey] - Optional Azure Functions key; defaults to REACT_APP_INTERVIEW_API_KEY
 * @returns {Promise<Object>} The JSON response from the backend
 */
export const validateInterview = async (options = {}) => {
  const {
    sessionId,
    isUserValidated,
    apiBase,
    functionsKey,
  } = options;

  if (!sessionId) {
    throw new Error('validateInterview: sessionId is required');
  }

  if (typeof isUserValidated !== 'boolean') {
    throw new Error('validateInterview: isUserValidated (boolean) is required');
  }

  const base = apiBase || INTERVIEW_API_BASE;
  if (!base) {
    throw new Error(
      'Interview API base URL not set. Provide REACT_APP_INTERVIEW_API_BASE or pass options.apiBase.'
    );
  }

  try {
    const headers = { 'Content-Type': 'application/json' };
    const key = process.env.REACT_APP_INTERVIEW_API_KEY || functionsKey || INTERVIEW_FUNCTIONS_KEY;
    if (key) headers['x-functions-key'] = key;
    const url = key
      ? `${base}/interview/validate?code=${encodeURIComponent(key)}`
      : `${base}/interview/validate`;

    const res = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ sessionId, isUserValidated }),
    });

    const result = await res.json();

    if (!res.ok || result?.success === false) {
      logger.error('Error validating interview:', result);
      throw new Error(result?.error || 'Failed to validate interview');
    }

    return result;
  } catch (error) {
    logger.error('Exception validating interview:', error?.message || error);
    throw error;
  }
};

/**
 * Submit preamble answers for analysis and storage when user stops interview
 * @param {Object} preambleData - Preamble submission data
 * @param {string} preambleData.interviewId - Interview ID
 * @param {string} preambleData.canvassId - Canvass ID  
 * @param {string} preambleData.strategyOutline - Strategy outline from canvass
 * @param {Object} preambleData.constraints - Constraints from canvass
 * @param {Array} preambleData.preambleQuestions - Preamble questions array
 * @param {Object} preambleData.preambleAnswers - Preamble answers object
 * @param {string} preambleData.assistantId - Assistant ID
 * @returns {Promise<Object>} - Submission result
 */
export const submitPreambleAnswers = async (options = {}) => {
  try {
    const {
      apiBase,
      functionsKey,
      preambleData,
    } = options;
    const headers = { 'Content-Type': 'application/json' };
    const key = functionsKey || INTERVIEW_FUNCTIONS_KEY;
    if (key) headers['x-functions-key'] = key;

    const base = apiBase || INTERVIEW_API_BASE;
    if (!base) {
      throw new Error(
        'Interview API base URL not set. Provide REACT_APP_INTERVIEW_API_BASE or pass options.apiBase.'
      );
    }
    const url = key ? `${base}/preamble?code=${encodeURIComponent(key)}` : `${base}/preamble`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(preambleData)
    });
    
    if (!response.ok) {
      throw new Error(`Preamble submission failed: ${response.statusText}`);
    }
    
    const result = await response.json();
    logger.log('Preamble submission successful:', result);
    return result;
    
  } catch (error) {
    logger.error('Error submitting preamble answers:', error);
    throw error;
  }
};

/**
 * Delete an interview by interviewId.
 * Backend route (Azure Function):
 *   DELETE {base}/delete/{interviewId}
 *
 * @param {Object} options
 * @param {string} options.interviewId - Required interview id
 * @param {string} [options.apiBase] - Optional override for base URL
 * @param {boolean} [options.returnFull=false] - When true, returns full payload with deletion metadata
 * @returns {Promise<Object|string>} Deletion message or full payload when returnFull=true
 */
export const deleteInterview = async (options = {}) => {
  const { interviewId, apiBase, returnFull = false } = options;

  if (!interviewId) {
    throw new Error('deleteInterview: interviewId is required');
  }

  const base = apiBase || INTERVIEW_API_BASE;
  if (!base) {
    throw new Error(
      'Interview API base URL not set. Provide REACT_APP_INTERVIEW_API_BASE or pass options.apiBase.'
    );
  }

  try {
    // Add authentication headers
    const token = await getAccessToken();
    const headers = { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };

    const url = `${base}/interview/delete/${encodeURIComponent(interviewId)}`;

    const res = await fetch(url, {
      method: 'DELETE',
      headers,
    });
    const result = await res.json();

    if (!res.ok || result?.success === false) {
      logger.error('Error deleting interview:', result);
      throw new Error(result?.error || result?.message || 'Failed to delete interview');
    }

    if (returnFull) return result;
    return result?.message ?? null;
  } catch (error) {
    logger.error('Exception deleting interview:', error?.message || error);
    throw error;
  }
};

/**
 * Fetch a single interview by sessionId.
 * Backend route (Azure Function):
 *   GET {base}/interview/get?sessionId=<sessionId>
 * See: Functions_Backend/Interview_Service/src/functions/InterviewChat.ts (get-interview)
 *
 * @param {Object} options
 * @param {string} options.sessionId - Required interview session id
 * @param {string} [options.apiBase] - Optional override for base URL
 * @returns {Promise<Object>} The interview data with messages and insights
 */
export const fetchInterviewBySessionId = async (options = {}) => {
  const { sessionId, apiBase } = options;

  if (!sessionId) {
    throw new Error('fetchInterviewBySessionId: sessionId is required');
  }

  const base = apiBase || INTERVIEW_API_BASE;
  if (!base) {
    throw new Error(
      'Interview API base URL not set. Provide REACT_APP_INTERVIEW_API_BASE or pass options.apiBase.'
    );
  }

  const params = new URLSearchParams();
  params.append('sessionId', String(sessionId));

  const url = `${base}/interview/get?${params.toString()}`;

  try {
    // Add authentication headers
    const token = await getAccessToken();
    const headers = { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };

    const res = await fetch(url, {
      method: 'GET',
      headers,
    });
    const result = await res.json();

    if (!res.ok || result?.success === false) {
      logger.error('Error fetching interview by sessionId:', result);
      throw new Error(result?.error || 'Failed to fetch interview');
    }

    return result;
  } catch (error) {
    logger.error('Exception fetching interview by sessionId:', error?.message || error);
    throw error;
  }
};

/**
 * Submit a free-form user thought/explanation to be appended to the interview messages.
 * Backend route (Azure Function):
 *   POST {base}/interview/thought
 * See: Functions_Backend/Interview_Service/src/functions/InterviewChat.ts (add-interview-thought)
 *
 * @param {Object} options
 * @param {string} options.sessionId - Required interview session id
 * @param {string} options.canvassId - Canvass / voice id
 * @param {string} options.text - Required free-form text from the user
 * @param {string} [options.apiBase] - Optional override for base URL
 * @param {string} [options.functionsKey] - Optional Azure Functions key; defaults to REACT_APP_INTERVIEW_API_KEY
 * @returns {Promise<Object>} The JSON response from the backend
 */
export const submitInterviewThought = async (options = {}) => {
  const {
    sessionId,
    canvassId,
    text,
    apiBase,
    functionsKey,
  } = options;

  if (!sessionId) {
    throw new Error('submitInterviewThought: sessionId is required');
  }

  if (!text || typeof text !== 'string' || text.trim() === '') {
    throw new Error('submitInterviewThought: non-empty text is required');
  }

  const base = apiBase || INTERVIEW_API_BASE;
  if (!base) {
    throw new Error(
      'Interview API base URL not set. Provide REACT_APP_INTERVIEW_API_BASE or pass options.apiBase.'
    );
  }

  try {
    const headers = { 'Content-Type': 'application/json' };
    const key = process.env.REACT_APP_INTERVIEW_API_KEY || functionsKey;
    if (key) headers['x-functions-key'] = key;
    const url = key
      ? `${base}/interview/thought?code=${encodeURIComponent(key)}`
      : `${base}/interview/thought`;

    const res = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ sessionId, canvassId, text: text.trim() }),
    });

    const result = await res.json();

    if (!res.ok || result?.success === false) {
      logger.error('Error submitting interview thought:', result);
      throw new Error(result?.error || 'Failed to submit thought');
    }

    return result;
  } catch (error) {
    logger.error('Exception submitting interview thought:', error?.message || error);
    throw error;
  }
};

export default {
  fetchInterviewsByCanvassId,
  fetchAllInterviewsByCanvassId,
  fetchInterviewBySessionId,
  validateInterview,
  submitPreambleAnswers,
  submitInterviewThought
};